(() => {
  const PAGE_SIZE = 36;
  const ALL_FINISHES = 'All finishes';
  const openingCodes = ['S024', 'C19', 'J02', 'N04', 'D05', 'S006'];
  const finishDescriptions = {
    'All finishes': 'Explore the full colour library, from everyday solids to light-catching effects.',
    'Solid colours': 'Everyday neutrals, deep tones and statement colour in a solid finish.',
    Glitter: 'A little sparkle, with glitter finishes that catch the light.',
    'Cat Eye': 'Magnetic finishes that create a light-catching effect with a nail magnet.',
    Disco: 'Reflective sparkle for a manicure that catches the light.',
    Neon: 'Bright, vivid colour for a bolder manicure.',
    Jelly: 'Translucent colour with a sheer, buildable look.',
  };
  const normaliseCode = (value) => String(value || '').trim().toUpperCase()
    .replace(/^([A-Z]+)0*(\d+)$/, (_, prefix, digits) => prefix + Number(digits));
  const normaliseText = (value) => String(value || '').normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  const matchesQuery = (shade, query) => {
    const terms = normaliseText(query).split(/\s+/).filter(Boolean);
    const words = normaliseText(`${shade.name} ${shade.code} ${shade.category}`);
    return terms.every((term) => /^[a-z]+\d+$/i.test(term)
      ? normaliseCode(term) === normaliseCode(shade.code)
      : words.includes(term));
  };
  const orderEntries = (entries, finish) => {
    if (finish !== ALL_FINISHES) return entries;
    const opening = openingCodes.map((code) => normaliseCode(code));
    const featured = opening.map((code) => entries.find((entry) => normaliseCode(entry.shade.code) === code)).filter(Boolean);
    const included = new Set(featured);
    return [...featured, ...entries.filter((entry) => !included.has(entry))];
  };
  const safeURL = (value) => {
    if (!value || typeof value !== 'string') return '';
    try {
      const url = new URL(value, window.location.href);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch {
      return '';
    }
  };

  const initialise = async (root) => {
    if (root.dataset.initialised) return;
    root.dataset.initialised = 'loading';
    const loading = root.querySelector('[data-shade-loading]');
    const grid = root.querySelector('[data-shade-grid]');
    const controls = root.querySelector('.meroo-shade-library__controls');
    const results = root.querySelector('[data-shade-results]');
    const empty = root.querySelector('[data-shade-empty]');
    const count = root.querySelector('[data-shade-count]');
    const filters = root.querySelector('[data-shade-filters]');
    const search = root.querySelector('[data-shade-search]');
    const available = root.querySelector('[data-shade-available]');
    const more = root.querySelector('[data-shade-more]');
    const progress = root.querySelector('[data-shade-progress]');
    const dialog = root.querySelector('[data-shade-dialog]');
    const finishTitle = root.querySelector('[data-shade-finish-title]');
    const finishDescription = root.querySelector('[data-shade-finish-description]');
    grid.setAttribute('aria-busy', 'true');
    loading.hidden = false;

    let shades;
    let products;
    try {
      const response = await fetch(root.dataset.source);
      if (!response.ok) throw new Error('Shade library unavailable');
      shades = await response.json();
      if (!Array.isArray(shades) || !shades.length || shades.some((shade) =>
        !['code', 'name', 'category', 'asset'].every((key) => typeof shade[key] === 'string' && shade[key]))) {
        throw new Error('Invalid shade references');
      }
      const productData = JSON.parse(root.querySelector('[data-shade-products]').textContent);
      if (!Array.isArray(productData)) throw new Error('Invalid product data');
      products = new Map();
      for (const product of productData) {
        const code = normaliseCode(product.code);
        const url = safeURL(product.url);
        if (!code || !url) continue;
        const existing = products.get(code);
        if (!existing || (!existing.available && product.available)) {
          products.set(code, { ...product, url, image: safeURL(product.image), available: product.available === true });
        }
      }
    } catch {
      root.dataset.initialised = '';
      grid.setAttribute('aria-busy', 'false');
      loading.setAttribute('role', 'alert');
      loading.textContent = 'The colour library could not load. Please try again, or browse products from the shop menu. ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'meroo-shade-library__retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', () => {
        loading.textContent = 'Loading the colour library…';
        loading.removeAttribute('role');
        initialise(root);
      }, { once: true });
      loading.append(retry);
      return;
    }
    if (!root.isConnected) return;

    let selectedFinish = ALL_FINISHES;
    let visibleLimit = PAGE_SIZE;
    let matchedEntries = [];
    let lastPreview = null;
    const categories = [ALL_FINISHES, ...new Set(shades.map((shade) => shade.category))];
    const finishButtons = Array.from(root.querySelectorAll('[data-shade-finish]'));
    const swatchURL = (shade) => safeURL(root.dataset.assetBase + shade.asset);
    const descriptionFor = (finish) => finishDescriptions[finish] || `Explore the ${finish.toLocaleLowerCase()} colour references.`;

    const closeDialog = () => {
      if (dialog.open) dialog.close();
    };
    dialog.querySelector('[data-shade-dialog-close]').addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
        closeDialog();
      }
    });
    dialog.addEventListener('close', () => {
      if (lastPreview?.isConnected && !lastPreview.closest('.meroo-shade-tile').hidden) {
        lastPreview.focus({ preventScroll: true });
      }
    });

    const openPreview = (entry) => {
      const { shade, product, preview } = entry;
      lastPreview = preview;
      const image = dialog.querySelector('[data-shade-dialog-image]');
      image.src = swatchURL(shade);
      image.alt = `${shade.name}, ${shade.code}, from the MEROO colour chart`;
      dialog.querySelector('[data-shade-dialog-code]').textContent = shade.code;
      dialog.querySelector('[data-shade-dialog-title]').textContent = shade.name;
      dialog.querySelector('[data-shade-dialog-finish]').textContent = shade.category;
      dialog.querySelector('[data-shade-dialog-description]').textContent = descriptionFor(shade.category);
      const productImage = dialog.querySelector('[data-shade-dialog-product-image]');
      productImage.hidden = !product?.image;
      if (product?.image) {
        productImage.src = product.image;
        productImage.alt = product.title || `${shade.name} product`;
        productImage.onerror = () => { productImage.hidden = true; };
      } else {
        productImage.removeAttribute('src');
        productImage.alt = '';
      }
      const link = dialog.querySelector('[data-shade-dialog-link]');
      link.hidden = !product;
      if (product) {
        link.href = product.url;
        link.textContent = product.available ? 'Shop this shade' : 'View product';
      } else {
        link.removeAttribute('href');
      }
      dialog.querySelector('[data-shade-dialog-status]').textContent = product
        ? [product.available ? 'Available to shop' : 'Currently unavailable', product.price].filter(Boolean).join(' · ')
        : 'Colour reference only. This shade does not currently have a product page.';
      dialog.showModal();
    };

    const entries = shades.map((shade) => {
      const product = products.get(normaliseCode(shade.code));
      const card = document.createElement('article');
      card.className = 'meroo-shade-tile';
      card.hidden = true;
      const preview = document.createElement('button');
      preview.type = 'button';
      preview.className = 'meroo-shade-tile__preview';
      preview.setAttribute('aria-label', `Preview ${shade.name}, ${shade.code}`);
      preview.setAttribute('aria-haspopup', 'dialog');
      if (dialog.id) preview.setAttribute('aria-controls', dialog.id);
      const image = document.createElement('img');
      image.className = 'meroo-shade-tile__swatch';
      image.src = swatchURL(shade);
      image.alt = '';
      image.width = 240;
      image.height = 125;
      image.loading = 'lazy';
      image.decoding = 'async';
      const code = document.createElement('span');
      code.className = 'meroo-shade-tile__code';
      code.textContent = shade.code;
      preview.append(image, code);
      const title = document.createElement('h3');
      title.className = 'meroo-shade-tile__name';
      title.textContent = shade.name;
      const status = document.createElement('p');
      status.className = 'meroo-shade-tile__status';
      status.textContent = product
        ? [shade.category, product.available ? product.price : 'Currently unavailable'].filter(Boolean).join(' · ')
        : shade.category;
      const shop = document.createElement(product ? 'a' : 'span');
      shop.className = 'meroo-shade-tile__shop';
      shop.textContent = product ? (product.available ? 'Shop shade' : 'View product') : 'Colour reference';
      if (product) {
        shop.href = product.url;
        shop.setAttribute('aria-label', `${product.available ? 'Shop' : 'View'} ${shade.name}, ${shade.code}`);
      }
      card.append(preview, title, status, shop);
      const entry = { card, preview, shade, product };
      preview.addEventListener('click', () => openPreview(entry));
      return entry;
    });

    const update = ({ resetLimit = true } = {}) => {
      if (resetLimit) visibleLimit = PAGE_SIZE;
      const ordered = orderEntries(entries, selectedFinish);
      matchedEntries = ordered.filter((entry) =>
        (selectedFinish === ALL_FINISHES || entry.shade.category === selectedFinish)
        && (!available.checked || entry.product?.available)
        && matchesQuery(entry.shade, search.value));
      const shown = matchedEntries.slice(0, visibleLimit);
      const visible = new Set(shown);
      for (const entry of entries) entry.card.hidden = !visible.has(entry);
      // Moving the existing nodes preserves their listeners and never duplicates source shades.
      grid.append(...ordered.map((entry) => entry.card));
      count.textContent = `${matchedEntries.length} ${matchedEntries.length === 1 ? 'shade' : 'shades'}`;
      empty.hidden = matchedEntries.length !== 0;
      progress.textContent = `Showing ${shown.length} of ${matchedEntries.length} ${matchedEntries.length === 1 ? 'shade' : 'shades'}`;
      more.hidden = shown.length >= matchedEntries.length;
      for (const button of filters.querySelectorAll('button')) {
        button.setAttribute('aria-pressed', String(button.dataset.finish === selectedFinish));
      }
      for (const button of finishButtons) {
        button.setAttribute('aria-pressed', String(button.dataset.shadeFinish === selectedFinish));
      }
      finishTitle.textContent = selectedFinish;
      finishDescription.textContent = descriptionFor(selectedFinish);
    };

    filters.replaceChildren();
    for (const category of categories) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.finish = category;
      button.textContent = category;
      button.addEventListener('click', () => {
        selectedFinish = category;
        update();
      });
      filters.append(button);
    }
    for (const button of finishButtons) {
      button.addEventListener('click', (event) => {
        if (!categories.includes(button.dataset.shadeFinish)) return;
        selectedFinish = button.dataset.shadeFinish;
        search.value = '';
        available.checked = false;
        update();
        if (event.detail === 0) results.focus({ preventScroll: true });
        results.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
      });
    }
    search.addEventListener('input', () => update());
    available.addEventListener('change', () => update());
    for (const reset of root.querySelectorAll('[data-shade-reset]')) {
      reset.addEventListener('click', () => {
        selectedFinish = ALL_FINISHES;
        search.value = '';
        available.checked = false;
        update();
        search.focus({ preventScroll: true });
      });
    }
    more.addEventListener('click', (event) => {
      const firstNew = matchedEntries[Math.min(visibleLimit, matchedEntries.length)];
      visibleLimit += PAGE_SIZE;
      update({ resetLimit: false });
      if (firstNew && (event.detail === 0 || (more.hidden && document.activeElement === more))) {
        firstNew.preview.focus({ preventScroll: true });
        firstNew.preview.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
    });
    controls.hidden = false;
    results.hidden = false;
    loading.hidden = true;
    grid.setAttribute('aria-busy', 'false');
    root.dataset.initialised = 'true';
    update();
  };

  const initialiseWithin = (scope) => {
    if (scope.matches?.('[data-shade-library]')) initialise(scope);
    scope.querySelectorAll('[data-shade-library]').forEach(initialise);
  };
  initialiseWithin(document);
  document.addEventListener('shopify:section:load', (event) => initialiseWithin(event.target));
})();
